// @flow
import MediaPicker from './MediaLoader/MediaPicker'

import React, { Component, type ComponentType } from 'react'
import FileUpload from '../controls/FileUpload'
import styled from 'styled-components'
import _ from 'lodash'
import type { Media, ImageSizeRestrictions, PersistedMedia } from '../types'
import { translateFunc, translations } from '../../shared/localize'
import ErrorText from '../controls/ErrorText'
import Tooltip from '../Tooltip'
import type { SelectResult } from './MediaLoader/MediaPicker'
import Q from 'q'

const Wrapper: ComponentType<Object> = styled.div`
  display: inline-block;
  position: relative;
`
const ForTests = styled.div`
  display: none;
`

type Props = {|
  authenticityToken: string,
  media: ?PersistedMedia,
  height?: number,
  width?: number,
  errors?: Array<string>,
  onMediasLoad: (Array<Media>) => void,
  className?: string,
  skipMediaUpload: boolean,
  imageSizeRestrictions?: ImageSizeRestrictions,
  withoutMediaPreview?: boolean,
  title?: string,
  titleFontSize?: number,
  mediaType: 'image' | 'video',
  multiple?: boolean,
  maxImageSize?: ?number
|}

type State = {|
  fileUploadCallbacks: Array<{| file: File, resolve: (Media) => void, reject: () => void |}>,
  uploadedCount: number,
  imagePreview: ?Media,
  isLoading: boolean,
  errors: { [string]: Array<string> }
|}

const t = translateFunc(translations.single_media_loader)

const imageUrl = '/api/v1/images'
const videoUrl = '/api/v1/videos'
const imageParamName = 'image[picture]'
const videoParamName = 'video[media]'

export default class MediaLoader extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      fileUploadCallbacks: [],
      uploadedCount: 0,
      imagePreview: null,
      isLoading: false,
      errors: {}
    }
  }

  render() {
    const {
      media,
      authenticityToken,
      className,
      height,
      width,
      skipMediaUpload,
      title,
      titleFontSize,
      mediaType
    } = this.props
    const { isLoading } = this.state

    const errors = _.reject(_.concat(this.props.errors || []), _.isEmpty)
    const currentMedia = mediaType === 'image' ? this.currentImage() : this.currentVideo()
    const tooltipContent = this.tooltipContent()
    return (
      <Wrapper className={`${className || ''} t-MediaLoader`}>
        {isLoading && <div className="t-MediaLoader__loading" />}
        {media && (
          <ForTests
            className={
              't-MediaLoader__media-name' + (media.id ? ' t-MediaLoader__media-name_loaded' : '')
            }
          >
            {media.name}
          </ForTests>
        )}
        <Tooltip
          isOpen={!!tooltipContent}
          onClose={() => this.setState({ errors: {} })}
          content={tooltipContent}
        >
          <MediaPicker
            media={currentMedia}
            isLoading={isLoading}
            height={height}
            width={width}
            errors={errors}
            title={title}
            titleFontSize={titleFontSize}
            {...this.props.imageSizeRestrictions}
            notes={skipMediaUpload ? [] : this.mediaRestrictions()}
            skipImageValidation={skipMediaUpload}
            withoutMediaPreview={this.props.withoutMediaPreview}
            mediaType={mediaType}
            multiple={this.props.multiple}
            maxImageSize={this.props.maxImageSize || 2}
            onSelect={(result: SelectResult) => this.mediaSelected(result)}
          />
        </Tooltip>
        {_.map(this.state.fileUploadCallbacks, (fileUploadCallbackSet, index: number) => {
          return (
            <FileUpload
              key={index}
              url={mediaType === 'image' ? imageUrl : videoUrl}
              file={fileUploadCallbackSet.file}
              fileParamName={mediaType === 'image' ? imageParamName : videoParamName}
              params={{ authenticity_token: authenticityToken }}
              onSuccess={(response) =>
                this.loaded(
                  response,
                  fileUploadCallbackSet.file,
                  fileUploadCallbackSet.resolve,
                  fileUploadCallbackSet.reject
                )
              }
              onProgress={() => {}}
              onError={(error) => this.loadFailed(error, fileUploadCallbackSet.reject)}
            />
          )
        })}
      </Wrapper>
    )
  }

  loaded(response: string, file: any, resolve: (Media) => void, reject: () => void) {
    try {
      const parsed = JSON.parse(response)

      const { success } = parsed

      this.setState({ imagePreview: null })
      if (success) {
        const media = parsed.data

        this.setState({ uploadedCount: this.state.uploadedCount + 1 })
        resolve(media)
      } else {
        const { message } = parsed
        this.setState({
          errors: { ...this.state.errors, [file.name]: this.errorResponseToStateErrors(message) }
        })
        reject()
      }
    } catch (e) {
      this.setState({
        imagePreview: null,
        errors: { ...this.state.errors, [file.name]: [e.toString()] }
      })
      reject()
    }
  }

  errorResponseToStateErrors(message: any): Array<string> {
    if (_.isArray(message)) return message
    if (_.isObject(message) && message.picture) return message.picture
    if (_.isString(message)) return [message]

    return ["Can't get error from response"]
  }

  loadFailed(error: any, reject: () => void) {
    console.warn(error)
    reject()
  }

  mediaSelected(pickerResult: SelectResult) {
    const { skipMediaUpload, mediaType } = this.props
    if (mediaType === 'image') {
      this.setState({
        imagePreview: pickerResult.success.length === 0 ? null : pickerResult.success[0].media
      })
    }

    _.each(pickerResult.failed, (failedResult) => {
      this.setState({
        errors: { ...this.state.errors, [failedResult.file.name]: failedResult.errors }
      })
    })
    if (skipMediaUpload) {
      if (mediaType === 'image') {
        this.setState({ imagePreview: null })
      }
      this.props.onMediasLoad(_.map(pickerResult.success, (successResult) => successResult.media))
    } else {
      const filesToUpload = pickerResult.success.map((successResult) => successResult.file)
      this.setState({ isLoading: true })
      Q.allSettled(_.map(filesToUpload, (file) => this.uploadFile(file))).then((results) => {
        const medias = _.map(
          _.filter(results, (res) => res.state === 'fulfilled'),
          (res) => res.value
        )
        if (medias.length > 0) this.props.onMediasLoad(medias)
        this.setState({ isLoading: false, uploadedCount: 0, fileUploadCallbacks: [] })
      })
    }
  }

  uploadFile(file: File): Promise<Function> {
    return new Promise((resolve, reject) => {
      this.setState({
        fileUploadCallbacks: [...this.state.fileUploadCallbacks, { file, resolve, reject }]
      })
    })
  }

  mediaWithoutId(video: PersistedMedia): Media {
    return {
      src: video.src,
      name: video.name,
      width: video.width,
      height: video.height,
      size: video.size
    }
  }

  mediaRestrictions(): Array<string> {
    const { imageSizeRestrictions, maxImageSize } = this.props
    if (imageSizeRestrictions) {
      const { minWidth, minHeight, maxWidth, maxHeight } = imageSizeRestrictions
      return [
        t('size_restrictions', { minWidth, minHeight, maxWidth, maxHeight }),
        t('less_than_mb', { size: maxImageSize || 2 })
      ]
    }

    return [t('less_than_mb', { size: 10 })]
  }

  currentImage(): ?Media {
    const { media } = this.props
    const { imagePreview } = this.state
    return imagePreview || (media && this.mediaWithoutId(media))
  }

  currentVideo(): ?Media {
    const { media } = this.props
    return media && this.mediaWithoutId(media)
  }

  tooltipContent(): any {
    if (this.state.isLoading && this.state.fileUploadCallbacks.length > 1) {
      return <span>{t('files_loaded', { count: this.state.uploadedCount })}</span>
    }
    const errorsNode = _.map(this.state.errors, (errors, fileName: string) => (
      <ErrorText key={fileName} errors={_.map(errors, (error) => `${fileName}: ${error}`)} />
    ))
    return _.isEqual(errorsNode, []) ? null : errorsNode
  }
}
